import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useOutlet } from "@/context/OutletContext";

const AuthContext = createContext(null);

export function AuthProvider({ children, enabled = true }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const outletCtx = useOutlet();

  // Once user loads, sync accessible outlets into OutletContext + choose active
  const syncOutletsFromUser = (u) => {
    if (!u || !u.outlets || !outletCtx) return;
    if (u.outlets.length > 0) {
      outletCtx.setOutlets(u.outlets);
      // For outlet_admin: activeOutlet is fixed to their outlet
      // For super_admin: read last active from localStorage or default to first
      const savedId = localStorage.getItem("resto_active_outlet");
      const chosen = u.platform_role === "super_admin"
        ? (u.outlets.find(o => o.id === savedId) || u.outlets[0])
        : u.outlets[0];
      if (chosen) {
        outletCtx.setActiveOutlet((cur) => ({ ...cur, ...chosen }));
        localStorage.setItem("resto_active_outlet", chosen.id);
      }
    }
  };

  useEffect(() => {
    if (!enabled) {
      setUser(false);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setUser(null);
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (cancelled) return;
        setUser(data);
        syncOutletsFromUser(data);
      } catch {
        if (cancelled) return;
        setUser(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("resto_token", data.access_token);
    setUser(data);
    syncOutletsFromUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("resto_token");
    localStorage.removeItem("resto_active_outlet");
    setUser(false);
  };

  const isSuperAdmin = !!user && user.platform_role === "super_admin";

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
